import moment from "moment-timezone";
import jsPDF, { AcroFormCheckBox, AcroFormTextField } from "jspdf";
import { applyPlugin, Cell, CellHookData } from "jspdf-autotable";
import { font1 } from "fonts/Inter-normal.jsx";
import { font2 } from "fonts/Inter-bold.jsx";

import * as M from "reports/measures/Measure";
import * as MD from "reports/measures/MeasureDef";
import * as R from "reports/Report";
import * as RD from "reports/ReportDef";
import * as SR from "reports/measures/SeverityResolver";
import * as T from "reports/measures/Trajectory";

applyPlugin(jsPDF);

// ---=== Utilities ===--- //

export const generateReportPdfFileName = (
    patientNameMasked: string,
    reportWeek: RD.ReportWeek,
    preview: boolean = true,
    date: Date = null,
    patientId: number,
    payerType: RD.PayerEnums
): string => {
    if (!preview && date === null) {
        console.error("Date is required for approved Outcome Reports!");
    }

    // Synthesize filename-safe identifier directly from patient ID and payer type
    const filenameIdentifier = payerType === RD.PayerEnums.VA || !patientNameMasked
        ? `Patient${patientId}`
        : patientNameMasked.replace(/ /g, "_");

    return `${preview ? "PREVIEW_" : ""}Outcomes_${filenameIdentifier}_${R.isMonth6Report(reportWeek) ? "6MonthsPost" : `Week${reportWeek}`}_${moment(date ?? new Date()).format("YYYY_MM_DD")}.pdf`;
};

const processText = (text: string) => {
    return text.split(/(<[^>]+>)/).filter(part => part.trim() !== "");
};

// const getGraphCanvas = (chartRef: React.RefObject<ChartJS<"line", number[]>>) => {
//     let errorReasons = [];
//
//     try {
//         const chartInstance: ChartJS<"line", number[]> = chartRef.current;
//         if (!chartInstance) {
//             errorReasons.push(`No Chart instance found in chartRef.current.`);
//             throw new Error(`Cannot render key improvements graph: No chart instance.`);
//         }
//         else if (!chartInstance.canvas) {
//             errorReasons.push(`No canvas found in the Chart referenced by chartRef.current.`);
//             throw new Error(`Cannot render key improvements graph: No canvas.`);
//         }
//
//         const canvasDataURL: string = chartInstance.canvas.toDataURL("image/png")
//
//         if (!canvasDataURL) {
//             errorReasons.push(`Unable obtain dataURL for the Chart referenced by chartRef.current.`);
//             throw new Error(`Cannot render key improvements graph: No canvas data URL.`);
//         }
//
//         return canvasDataURL;
//     }
//     catch (e) {
//         console.error("Graph Canvas Error:", e);
//         throw new Error(`Cannot render key improvements graph in this Outcome Report! [${errorReasons.join(", ")}]`);
//     }
// };

const numberReportPages = (pdfDoc) => {
    let pageWidth = pdfDoc.internal.pageSize.width;
    let pageHeight = pdfDoc.internal.pageSize.height;
    let pages = pdfDoc.internal.getNumberOfPages();

    pdfDoc.setFontSize(11);  //Optional
    pdfDoc.setFont("Inter", "normal");

    for (let j = 1; j < pages + 1; j++) {
        let verticalPos = pageHeight - 20;
        pdfDoc.setPage(j);
        pdfDoc.text(`Rewire Your Brain. Unlearn Your Pain`, 40, verticalPos, { align: "left" });
        pdfDoc.text(`${j}`, pageWidth - 40, verticalPos, { align: "right" });
        pdfDoc.setDrawColor(30, 170, 241);
        pdfDoc.setFillColor(30, 170, 241);
        verticalPos = pageHeight - 10;
        pdfDoc.rect(0, verticalPos, pageWidth, 10, "FD");
        pdfDoc.rect(0, 0, pageWidth, 10, "FD");
    }
};

const hasAnyIncludedTrajectories = (includedTrajectories: Record<MD.MeasureType, boolean>) => {
    return Object.values(includedTrajectories).some(isIncluded => isIncluded);
}

const validateNumericValue = (value: any, fallback: number = 0): number => {
    const num = Number(value);
    return isNaN(num) || !isFinite(num) ? fallback : num;
};

const validateCoordinates = (x: any, y: any, width?: any, height?: any) => {
    const validX = validateNumericValue(x);
    const validY = validateNumericValue(y);
    const validWidth = width !== undefined ? validateNumericValue(width, 10) : undefined;
    const validHeight = height !== undefined ? validateNumericValue(height, 10) : undefined;

    return { x: validX, y: validY, width: validWidth, height: validHeight };
};

// ---=== Trajectory Tables ===--- //

type TrajectoryCellLayout = {
    head?: string[][];
    body: string[][];
    emptyFontStyle?: string;
    headFontStyle?: string;
    bodyFontStyle?: string;
    emptyBodyTextColor?: string;
    assessmentTextColor?: string;
    severityTextColor?: string;
    fillColor?: string;
    measureWeekTextColor?: string;
};

type PdfTrajectoryRow = {
    trajectory: T.Trajectory;
    [key: `week${string}`]:  MD.Measure;
};

type PdfRomTrajectoryRow = {
    trajectory: T.Trajectory;
    firstMeasure?: MD.Measure;
    recentMeasure?: MD.Measure;
};

const TrajectoryTableColumns = {
    trajectory: 0
};

const TrajectoryCellRows = {
    MeasureScoreDescription: 0,
    TrajectoryAssessment: 1,
    TrajectorySeverity: 0,
    MeasureWeek: 1
}

const TrajectoryCellFontStyle = {
    Normal: "normal",
    Bold: "bold"
};

const TrajectoryCellTextColors = {
    // Defaults
    DefaultCellBodyColor: "#475467",
    // Measures
    MeasureName: "#475467",
    MeasureValue: "#475467",
    MeasureScoreDescription: "#475467",
    // Uncategorized
    Uncategorized: "#475467"
};

const TrajectoryTextColors: { [key in T.TrajectoryType]: string } = {
    [T.TrajectoryType.WORSENED]: "#c14b24",
    [T.TrajectoryType.UNCHANGED]: "#475467",
    [T.TrajectoryType.IMPROVED]: "#069454"
};

const TrajectorySeverityTextColors: { [key in SR.Severity]: string } = {
    [SR.Severity.LOW]: "#069454",
    [SR.Severity.MILD]: "#db6803",
    [SR.Severity.MODERATE]: "#db6803",
    [SR.Severity.SEVERE]: "#c14b24",
    [SR.Severity.VERY_SEVERE]: "#c14b24",
    [SR.Severity.EXTREME]: "#c14b24"
};

type TrajectoryPalette = {
    [key in T.TrajectoryType]: string;
};

export const trajectoryFillPalettes: TrajectoryPalette[] = [
    // Palette 0
    {
        [T.TrajectoryType.UNCHANGED]: "#FFFFFF", // White
        [T.TrajectoryType.IMPROVED]:  "#E6F5E6", // Original Orange/Peach Yellow
        [T.TrajectoryType.WORSENED]:  "#FFEBEE"  // Original Pink
    },
    // Palette 1
    {
        [T.TrajectoryType.UNCHANGED]: "#FFFFFF", // White
        [T.TrajectoryType.IMPROVED]:  "#F0FFF0", // Honeydew Green
        [T.TrajectoryType.WORSENED]:  "#FFF0F0"  // Snow Pink
    },
    // Palette 2
    {
        [T.TrajectoryType.UNCHANGED]: "#FFFFFF", // White
        [T.TrajectoryType.IMPROVED]:  "#F0FFF0", // Standard Pale Green
        [T.TrajectoryType.WORSENED]:  "#FFF0F0"  // Significantly paler pink
    },
    // Palette 3:
    {
        [T.TrajectoryType.UNCHANGED]: "#FFFFFF", // White
        [T.TrajectoryType.IMPROVED]:  "#F0FFF0", // Standard Pale Green
        [T.TrajectoryType.WORSENED]:  "#FFEBEE"  // Significantly paler pink
    }
];

type SeverityPalette = {
    [key in SR.Severity]: string;
};

export const severityFillPalettes: SeverityPalette[] = [
    // Palette 0
    {
        [SR.Severity.LOW]: "#E6F5E6",         // Original Green
        [SR.Severity.MILD]: "#FFF3E0",        // Original Orange/Peach Yellow
        [SR.Severity.MODERATE]: "#FFF3E0",    // Original Orange/Peach Yellow
        [SR.Severity.SEVERE]: "#FFEBEE",      // Original Pink
        [SR.Severity.VERY_SEVERE]: "#FFEBEE", // Original Pink
        [SR.Severity.EXTREME]: "#FFEBEE"      // Original Pink
    },
    // Palette 1
    {
        [SR.Severity.LOW]: "#F0FFF0",         // Honeydew Green
        [SR.Severity.MILD]: "#FFF8DC",        // Cornsilk Yellow
        [SR.Severity.MODERATE]: "#FFF8DC",    // Cornsilk Yellow
        [SR.Severity.SEVERE]: "#FFF0F0",      // Snow Pink
        [SR.Severity.VERY_SEVERE]: "#FFF0F0", // Snow Pink
        [SR.Severity.EXTREME]: "#FFF0F0"      // Snow Pink
    },
    // Palette 2
    {
        [SR.Severity.LOW]: "#E6F5E6",         // Standard Pale Green
        [SR.Severity.MILD]: "#FFFACD",        // Lemon Chiffon (Paler, less orange yellow)
        [SR.Severity.MODERATE]: "#FFFACD",    // Lemon Chiffon (Paler, less orange yellow)
        [SR.Severity.SEVERE]: "#FFF5F5",      // Significantly paler pink
        [SR.Severity.VERY_SEVERE]: "#FFF5F5", // Significantly paler pink
        [SR.Severity.EXTREME]: "#FFF5F5"      // Significantly paler pink
    },
    // Palette 3
    {
        [SR.Severity.LOW]: "#F0FFF0",         // Honeydew Green
        [SR.Severity.MILD]: "#FFFEE5",        // Very Pale Yellow
        [SR.Severity.MODERATE]: "#FFFEE5",    // Very Pale Yellow
        [SR.Severity.SEVERE]: "#FFF9F9",      // Extremely Pale Pink (near white)
        [SR.Severity.VERY_SEVERE]: "#FFF9F9", // Extremely Pale Pink (near white)
        [SR.Severity.EXTREME]: "#FFF9F9"      // Extremely Pale Pink (near white)
    }
];

const createPdfTrajectoryTableColumns = (
    reportWeek: RD.ReportWeek,
    reportPlanWeeks: number
): (MD.MeasureWeek)[] => {
    let reportWeekNumber: number = R.isMonth6Report(reportWeek) ? reportPlanWeeks : Number(reportWeek);
    const isMonth6 = R.isMonth6Report(reportWeek);

    // Start with base columns [1, 4, 8, 12], filtered by reportWeekNumber
    let trajectoriesTableColumns: (MD.MeasureWeek)[] = [1, 4, 8, 12].filter(
        (week: MD.MeasureWeek) => Number(week) <= reportWeekNumber
    );

    // Add additional columns based on reportWeekNumber
    if (reportWeekNumber > 12 && reportWeekNumber <= 16) {
        // For weeks 13-16: add the reportWeekNumber itself
        trajectoriesTableColumns.push(reportWeekNumber);
    }
    else if (reportWeekNumber > 16) {
        if (isMonth6) {
            // For 6-month reports > 16: add the closest week evenly divisible by 4
            const closestDivisibleBy4 = Math.floor(reportWeekNumber / 4) * 4;
            trajectoriesTableColumns.push(closestDivisibleBy4);
        }
        else {
            // For non-6 month reports > 16:
            if (reportWeekNumber % 4 === 0) {
                // If evenly divisible by 4: add a previous multiple of 4 and current reportWeekNumber
                const previousMultipleOf4 = reportWeekNumber - 4;
                trajectoriesTableColumns.push(previousMultipleOf4);
                trajectoriesTableColumns.push(reportWeekNumber);
            }
            else {
                // If not evenly divisible by 4: add the closest multiple of 4 and reportWeekNumber
                const closestDivisibleBy4 = Math.floor(reportWeekNumber / 4) * 4;
                trajectoriesTableColumns.push(closestDivisibleBy4);
                trajectoriesTableColumns.push(reportWeekNumber);
            }
        }
    }

    // For 6-month reports, add MD.Month6Token at the end
    if (isMonth6) {
        trajectoriesTableColumns.push(MD.Month6Token);
    }

    return trajectoriesTableColumns;
};

const createPdfTrajectoryRow = (
    measureType: MD.MeasureType,
    allTrajectories: T.Trajectories,
    includedTrajectories: Record<MD.MeasureType, boolean>,
    tableColumns: (MD.MeasureWeek)[]
): PdfTrajectoryRow => {
    if (!includedTrajectories[measureType]) {
        return null;
    }
    const trajectory: T.Trajectory = T.getTrajectoryOfType(measureType, allTrajectories);
    const rowCells: PdfTrajectoryRow = { trajectory: trajectory };
    tableColumns.forEach(
        (measureWeek: MD.MeasureWeek) => {
            rowCells[`week${measureWeek}`] = trajectory.measures.find(measure => measure.week === measureWeek) ?? null;
        }
    );
    return rowCells;
};

const createPdfRomTrajectoryRow = (
    measureType: MD.MeasureType,
    allTrajectories: T.Trajectories,
    includedTrajectories: Record<MD.MeasureType, boolean>
): PdfRomTrajectoryRow => {
    if (!includedTrajectories[measureType]) {
        return null;
    }
    const trajectory: T.Trajectory = T.getTrajectoryOfType(measureType, allTrajectories);
    return  {
        trajectory: trajectory,
        firstMeasure: trajectory.firstNonWeekZeroMeasure,
        recentMeasure: trajectory.comparedMeasure
    };
};

const createPdfTrajectoryRows = (
    measureTypes: MD.MeasureTypes,
    allTrajectories: any,
    includedTrajectories: any,
    tableColumns?: any
): any[] => {
    return measureTypes
        .map(type =>
            M.isRomType(type)
                ? createPdfRomTrajectoryRow(
                    type,
                    allTrajectories,
                    includedTrajectories
                )
                : createPdfTrajectoryRow(
                    type,
                    allTrajectories,
                    includedTrajectories,
                    tableColumns
                )
        )
        .filter(Boolean);
};

const isTrajectoryColumn = (colIndex: number): boolean => {
    return colIndex === TrajectoryTableColumns.trajectory;
};

const renderPdfTrajectoryCell = (pdfDoc: any, colIndex: number, cell: Cell): void => {

    cell.text = [];

    let cellLayout: TrajectoryCellLayout = {
        body: [["-"]],
        headFontStyle: isTrajectoryColumn(colIndex) ? TrajectoryCellFontStyle.Normal : TrajectoryCellFontStyle.Normal,
        bodyFontStyle: TrajectoryCellFontStyle.Normal,
        fillColor: null,
        emptyFontStyle: TrajectoryCellFontStyle.Normal,
        emptyBodyTextColor: TrajectoryCellTextColors.DefaultCellBodyColor
    };

    if (cell.raw !== null) {
        if (isTrajectoryColumn(colIndex)) {
            let trajectory: T.Trajectory = cell.raw as T.Trajectory;
            let isRomMeasure = M.isRomType(trajectory.measureType);
            let firstMeasure = T.firstMeasure(trajectory);
            let comparedMeasure = trajectory.comparedMeasure;

            let displayAssessment: string;
            let assessmentColor: string = TrajectoryTextColors[T.TrajectoryType.UNCHANGED];
            if (trajectory.measures.length === 1 || firstMeasure.week === comparedMeasure.week) {
                displayAssessment = `No Data after Week ${firstMeasure.week}`;
            }
            else {
                const precisionMeasureTypes: MD.MeasureType[] = [MD.MeasureTypesMap.WHODAS];
                const displayValueOptions: T.DisplayComparedDeltaOptions = {
                    precision: isRomMeasure ? 1 : precisionMeasureTypes.includes(trajectory.measureType) ? 2 : 0,
                    round: true,
                    absolute: true,
                    percent: isRomMeasure
                };
                let displayValue = T.displayComparedDelta(trajectory, displayValueOptions);
                let displayFirstWeek = M.isMonth6MeasureWeek(firstMeasure.week) ? "Month 6" : `Week ${firstMeasure.week.toString()}`;
                let displayComparedWeek = M.isMonth6MeasureWeek(comparedMeasure.week) ? "Month 6" : `${comparedMeasure.week.toString()}`;
                let displayValueRange = `${trajectory.bandTrajectory === T.TrajectoryType.UNCHANGED ? "" : ` ${displayValue}`}${isRomMeasure ? "" : ` (${displayFirstWeek} to ${displayComparedWeek})`}`;
                displayAssessment = `${T.displayTrajectoryType(trajectory.bandTrajectory)}${displayValueRange}`;
                assessmentColor = TrajectoryTextColors[trajectory.bandTrajectory];
            }

            cellLayout.head = [[trajectory.measureName]];
            cellLayout.body = [[[M.scoreDescription(trajectory.measures), M.scoreRangeDisplay(trajectory.measures)].join(" ")], [displayAssessment]];
            cellLayout.fillColor = trajectoryFillPalettes[3][trajectory.bandTrajectory];
            cellLayout.assessmentTextColor = assessmentColor;
        }
        else {
            let measure: MD.Measure = cell.raw as MD.Measure;
            let isRomMeasure = M.isRomType(measure.type);
            let resolver: SR.SeverityDetails = M.severityResolver(measure);
            let displaySeverityLabel: string = resolver.label;
            let displayMeasureWeek: string = M.isMonth6MeasureWeek(measure.week) ? "Month 6" : `Week ${measure.week.toString()}`;

            cellLayout.head = [[M.displayAsUnits(measure)]];
            cellLayout.body = isRomMeasure ? [[displaySeverityLabel], [displayMeasureWeek]] : [[displaySeverityLabel]];
            cellLayout.fillColor = severityFillPalettes[3][resolver.severity];
            cellLayout.severityTextColor = TrajectorySeverityTextColors[resolver.severity];
            cellLayout.measureWeekTextColor = TrajectoryCellTextColors.DefaultCellBodyColor;
        }
    }

    const commonTableConfig = {
        colSpan: 1,
        tableWidth: cell.width - 10,
        theme: "plain" as const,
        styles: {
            cellPadding: 1,
            cellWidth: cell.width - 10,
            halign: "center",
            valign: "top"
        }
    };

    const marginLeft = cell.x + (cell.width - commonTableConfig.tableWidth) / 2;
    const estimatedInnerHeight = 5 + (cellLayout.head ? 12 : 0) + (cellLayout.body.length * 10) + 5;
    const verticalOffset = (cell.height - estimatedInnerHeight) / 2;

    if (cellLayout.head) {
        pdfDoc.autoTable({
            ...commonTableConfig,
            head: cellLayout.head,
            body: cellLayout.body,
            margin: { left: marginLeft },
            startY: cell.y + Math.max(verticalOffset, 0),
            // Styles
            headStyles: {
                fontSize: 12,
                textColor: isTrajectoryColumn(colIndex) ? TrajectoryCellTextColors.MeasureName : TrajectoryCellTextColors.MeasureValue,
                fontStyle: cellLayout.headFontStyle,
                cellPadding: { top: 0, bottom: 0 }
            },
            bodyStyles: {
                fontSize: 8,
                fontStyle: cellLayout.bodyFontStyle
            },
            // TypeScript doesn't think this function is in use, but it is!
            didParseCell(data) {
                if (data.section === "body" && data.row.raw) {
                    if (cellLayout.fillColor) {
                        pdfDoc.setFillColor(cellLayout.fillColor);
                        pdfDoc.rect(cell.x, cell.y, cell.width, cell.height, "F");
                    }
                    switch (colIndex) {
                        case TrajectoryTableColumns.trajectory:
                            switch (data.row.index) {
                                case TrajectoryCellRows.MeasureScoreDescription:
                                    data.cell.styles.textColor = TrajectoryCellTextColors.MeasureScoreDescription;
                                    break;
                                case TrajectoryCellRows.TrajectoryAssessment:
                                    data.cell.styles.textColor = cellLayout.assessmentTextColor;
                                    break;
                                default:
                                    data.cell.styles.textColor = TrajectoryCellTextColors.Uncategorized;
                                    break;
                            }
                            break;
                        default:
                            switch (data.row.index) {
                                case TrajectoryCellRows.TrajectorySeverity:
                                    data.cell.styles.textColor = cellLayout.severityTextColor;
                                    break;
                                case TrajectoryCellRows.MeasureWeek:
                                    data.cell.styles.textColor = cellLayout.measureWeekTextColor;
                                    break;
                                default:
                                    data.cell.styles.textColor = TrajectoryCellTextColors.Uncategorized;
                                    break;
                            }
                            break;
                    }
                }
            }
        });
    }
    else {
        pdfDoc.autoTable({
            ...commonTableConfig,
            body: cellLayout.body,
            margin: { left: marginLeft },
            startY: cell.y + Math.max(verticalOffset, 0),
            // Styles
            bodyStyles: {
                fontSize: 14,
                textColor: cellLayout.emptyBodyTextColor,
                fontStyle: cellLayout.emptyFontStyle
            }
        });
    }
};

// ---=== Render ===--- //

const renderColorizedText = (doc: any, cell: Cell, text: string): void => {
    const cellCoords = validateCoordinates(cell.x, cell.y, cell.width, cell.height);
    let pageWidth = cellCoords.width;
    let parts = [];

    cell.text = [];

    let blocks = processText(text);
    blocks.forEach(e => {
        if (e.includes("<")) {
            parts.push(e);
        }
        else {
            let pieces = e.split(" ");
            parts = parts.concat(pieces);
        }
    });

    const xPos = cellCoords.x + 2;
    let yPos = cellCoords.y + (cellCoords.height / 2);

    let cursorX = xPos;
    doc.setFont("Inter", "normal");
    parts.forEach(
        part => {
            switch (part) {
                case "<b>":
                    doc.setFont("Inter", "bold");
                    break;
                case "</b>":
                    doc.setFont("Inter", "normal");
                    break;
                default: {
                    const colorMatch = part.match(/<(\d|red|orange|green)>/);
                    if (colorMatch) {
                        const color = colorMatch[1];
                        switch (color) {
                            case "0":
                            case "1":
                            case "2":
                            case "3":
                            case "4":
                                doc.setTextColor(R.customReportColors[parseInt(color)]);
                                break;
                            case "red":
                                doc.setTextColor("#ED5C2D");
                                break;
                            case "orange":
                                doc.setTextColor("#EF6820");
                                break;
                            case "green":
                                doc.setTextColor("#17B26A");
                                break;
                        }
                    }
                    else if (part.match(/<\/(\d|red|orange|green)>/)) {
                        doc.setTextColor("#344054");
                    }
                    else if (part && part !== "") {
                        const textWidth = validateNumericValue(doc.getTextWidth(part + " "), 10);
                        if (cursorX + textWidth > pageWidth) {
                            cursorX = xPos;
                            yPos += validateNumericValue(doc.getTextDimensions(part).h + 1, 12);
                            doc.text(part + " ", cursorX, yPos);
                            cursorX += textWidth;
                        }
                        else {
                            doc.text(part + "", cursorX, yPos);
                            cursorX += textWidth;
                        }
                    }
                    break;
                }
            }
        }
    );
};

const generateProviderSection = (pdfDoc, patientName: string, coach: string) => {
    let pageWidth = pdfDoc.internal.pageSize.width;
    let xOffset: number;
    let y = 30;

    pdfDoc.addPage();

    pdfDoc.setFont("Inter", "normal");
    pdfDoc.setFontSize(12);
    pdfDoc.setFillColor(234, 236, 240);
    pdfDoc.setDrawColor(234, 236, 240);
    pdfDoc.setTextColor(71, 84, 103);

    pdfDoc.line(40, y, ((pdfDoc.internal.pageSize.width / 2)) - (52), y);
    pdfDoc.line(((pdfDoc.internal.pageSize.width / 2)) + (52), y, (pageWidth - 40), y);
    xOffset = (pdfDoc.internal.pageSize.width / 2) - (pdfDoc.getStringUnitWidth(`Physician Review`) * pdfDoc.internal.getFontSize() / 2);
    pdfDoc.text(`Physician Review`, xOffset, y + 3);

    y += 25;

    pdfDoc.setFontSize(8);
    pdfDoc.setTextColor(71, 84, 103);
    xOffset = (pdfDoc.internal.pageSize.width / 2) - (pdfDoc.getStringUnitWidth(`For provider use only`) * pdfDoc.internal.getFontSize() / 2);
    pdfDoc.text(`For provider use only`, xOffset, y);

    y += 15;

    const checkboxY = y + 5;

    pdfDoc.setTextColor("black");
    const checkBox = new AcroFormCheckBox();
    checkBox.fieldName = "CheckBox1";
    checkBox.width = 10;
    checkBox.height = 10;
    checkBox.x = 40;
    checkBox.y = checkboxY;
    checkBox.fontSize = 10;
    checkBox.appearanceState = "Off";
    checkBox.value = "Off";
    checkBox.maxFontSize = 10;
    pdfDoc.addField(checkBox);

    y = checkboxY;

    // Checkbox Square Border
    pdfDoc.setDrawColor(207, 212, 220);
    pdfDoc.setLineWidth(1);
    pdfDoc.rect(40, y, 10, 10);

    pdfDoc.setFontSize(10);
    pdfDoc.setTextColor(52, 64, 83);

    pdfDoc.autoTable({
        pageBreak: "avoid",
        showHead: "firstPage",
        body: [[`Having reviewed the subjective and objective outcomes of ${patientName}, I concur with and hold as my own ${coach}'s assessment and onward plan of care.`]],
        startY: y - 5,
        theme: "plain",
        bodyStyles: { fontSize: 10, font: "Inter", textColor: "#344053" },
        tableWidth: "auto",
        styles: {
            overflow: "linebreak",
            cellWidth: "wrap",
            halign: "left"
        },
        margin: { left: 55, right: 40 }
    });

    y = pdfDoc.lastAutoTable.finalY;

    pdfDoc.setFontSize(14);
    pdfDoc.setTextColor(50, 96, 158);
    pdfDoc.text(40, y += 30, "Other Comments:");

    const notesFontSize = 10;
    const notesLineHeight = notesFontSize * 1.2;
    const notesMaxLines = 36;
    const notesHeight = notesMaxLines * notesLineHeight;

    pdfDoc.setFillColor(207, 212, 220);
    pdfDoc.setDrawColor(207, 212, 220);
    pdfDoc.rect(40, y += 10, 530, notesHeight);

    pdfDoc.setTextColor("black");
    const notesField = new AcroFormTextField();
    notesField.x = 40;
    notesField.y = y;
    notesField.width = 530;
    notesField.height = notesHeight;
    notesField.multiline = true;
    notesField.doNotScroll = true;
    notesField.fieldName = "NotesField";
    pdfDoc.addField(notesField);

    y += notesHeight;

    pdfDoc.setTextColor(52, 64, 84);
    const signatureField = new AcroFormTextField();
    signatureField.x = pageWidth - 270;
    signatureField.y = y += 40;
    signatureField.width = 230;
    signatureField.height = 30;
    signatureField.multiline = false;
    signatureField.doNotScroll = true;
    signatureField.textAlign = "right";
    signatureField.fieldName = "ProviderSignature";
    pdfDoc.addField(signatureField);

    pdfDoc.setLineWidth(2);
    pdfDoc.line(pageWidth - 270, y += 30, pageWidth - 40, y);

    pdfDoc.setFontSize(10);
    pdfDoc.setTextColor(128, 128, 128);
    pdfDoc.text(pageWidth - 40, y += 16, "Provider Signature", { align: "right" });

    pdfDoc.setTextColor(52, 64, 84);
    const dateField = new AcroFormTextField();
    dateField.x = pageWidth - 160;
    dateField.y = y += 20;
    dateField.width = 120;
    dateField.height = 20;
    dateField.multiline = false;
    dateField.doNotScroll = true;
    dateField.textAlign = "right";
    dateField.fieldName = "DateField";
    pdfDoc.addField(dateField);

    pdfDoc.setLineWidth(2);
    pdfDoc.line(pageWidth - 160, y += 20, pageWidth - 40, y);

    pdfDoc.setFontSize(10);
    pdfDoc.setTextColor(128, 128, 128);
    pdfDoc.text(pageWidth - 40, y + 16, "Date", { align: "right" });
};

export interface GeneratePdfParams {
    // Report Configuration
    brand: RD.BrandEnums;
    preparedDate: Date;
    preparerName: string;
    programType: string;
    reportPlanWeeks: number,
    reportWeek: MD.MeasureWeek;
    // Patient Info
    claimNumber: string;
    coach: string
    dateOfBirth: string;
    dateOfInjury: string;
    hasPhysicianOversight: boolean;
    patientId: number;
    patientName: string;
    payerType: RD.PayerEnums;
    physician: string;
    // Key Improvements Chart
    keyImprovementsChartImageData: string;
    // History of Injury
    historyOfInjury: string;
    // Subjective Outcomes
    discoveries: RD.Discoveries,
    engagementLevel: RD.EngagementLevel,
    goalsProgress: RD.GoalsProgress,
    goals: RD.Goals,
    subjectiveProgressSummary: string,
    // Objective Outcomes
    allTrajectories: T.Trajectories;
    averageActivityTimePerWeek: string,
    medicationReductionStatus: RD.MedicationReductionStatus,
    returnToWorkStatus: RD.ReturnToWorkStatus,
    sessionsAttended: number,
    uniqueDays: number,
    // Assessments
    behavioralAssessments: string,
    continuedNeedsAssessments: string,
    physicalAssessments: string,
    socialAssessments: string,
    // Plan
    plan: string,
    // Inclusions
    includeAverageTime: boolean,
    includeBehavioralAssessments: boolean,
    includeContinuedNeedsAssessments: boolean,
    includeEngagementLevel: boolean,
    includeGoalsProgress: boolean,
    includeMedicationReduction: boolean,
    includePhysicalAssessments: boolean,
    includeReturnToWork: boolean,
    includeSessionsAttended: boolean,
    includeSocialAssessments: boolean,
    includeUniqueDays: boolean,
    includedTrajectories: Record<MD.MeasureType, boolean>,
    selectedImprovedTrajectoryTypes: MD.MeasureTypes,
}

export const generatePdf = async ({
    // Report Configuration
    brand,
    preparedDate,
    preparerName,
    programType,
    reportPlanWeeks,
    reportWeek,
    // Patient Info
    claimNumber,
    coach,
    dateOfBirth,
    dateOfInjury,
    hasPhysicianOversight,
    patientId,
    patientName,
    payerType,
    physician,
    // Key Improvements Chart
    keyImprovementsChartImageData,
    // History of Injury
    historyOfInjury,
    // Subjective Outcomes
    discoveries,
    engagementLevel,
    goals,
    goalsProgress,
    subjectiveProgressSummary,
    // Objective Outcomes
    allTrajectories,
    averageActivityTimePerWeek,
    medicationReductionStatus,
    returnToWorkStatus,
    sessionsAttended,
    uniqueDays,
    // Assessments
    behavioralAssessments,
    continuedNeedsAssessments,
    physicalAssessments,
    socialAssessments,
    // Plan
    plan,
    // Inclusions
    includeAverageTime,
    includeBehavioralAssessments,
    includeContinuedNeedsAssessments,
    includeEngagementLevel,
    includeGoalsProgress,
    includeMedicationReduction,
    includePhysicalAssessments,
    includeReturnToWork,
    includeSessionsAttended,
    includeSocialAssessments,
    includeUniqueDays,
    includedTrajectories,
    selectedImprovedTrajectoryTypes
}: GeneratePdfParams): Promise<jsPDF> => {
    try {
        let callAddFont1 = function () {
            this.addFileToVFS("Inter-normal.ttf", font1);
            this.addFont("Inter-normal.ttf", "Inter", "normal");
        };
        jsPDF.API.events.push(["addFonts", callAddFont1]);

        let callAddFont2 = function () {
            this.addFileToVFS("Inter-bold.ttf", font2);
            this.addFont("Inter-bold.ttf", "Inter", "bold");
        };
        jsPDF.API.events.push(["addFonts", callAddFont2]);

        const pdfDoc: any = new jsPDF("p", "pt", "letter");

        let xOffset: number;
        let y = 10;
        let header: any[];
        let body: any[];

        pdfDoc.setLineHeightFactor(1.5);
        pdfDoc.setLineWidth(2);

        pdfDoc.setFontSize(20);
        let pageWidth = pdfDoc.internal.pageSize.width;
        let img = new Image();
        if (brand === RD.BrandEnums.KarunaHome) {
            img.src = "/icons/title.png";
            pdfDoc.addImage(img, "png", 250, y, 120, 100);
        }
        else if (brand === RD.BrandEnums.Reclaim) {
            img.src = "/icons/Reclaim-System-Stacked-Full-Color.png";
            y += 20;
            pdfDoc.addImage(img, "png", 190, y, 112 * 2, 41 * 2);
        }
        else {
            img.src = "/icons/ValorTitle.png";
            y += 20;
            pdfDoc.addImage(img, "png", 190, y, 112 * 2, 41 * 2);
        }

        // For VA patients, the patientName is already masked at this point
        pdfDoc.autoTable({
            pageBreak: "avoid",
            body: [[patientName]],
            startY: y + 100,
            theme: "plain",
            styles: { font: "Inter", fontSize: 25, textColor: [50, 96, 158], halign: "center", valign: "middle", fontStyle: "bold" }
        });
        y = pdfDoc.lastAutoTable.finalY;
        pdfDoc.setFont("Inter", "normal");

        // ---=== Patient Info ===--- //

        pdfDoc.setFontSize(12);
        y += 30;

        pdfDoc.setFillColor(234, 236, 240);
        pdfDoc.setDrawColor(234, 236, 240);
        pdfDoc.setTextColor(71, 84, 103);
        pdfDoc.line(40, y, ((pdfDoc.internal.pageSize.width / 2)) - (70), y);
        pdfDoc.line(((pdfDoc.internal.pageSize.width / 2)) + (70), y, (pageWidth - 40), y);
        xOffset = (pdfDoc.internal.pageSize.width / 2) - (pdfDoc.getStringUnitWidth(`Patient Outcome Report`) * pdfDoc.internal.getFontSize() / 2);
        pdfDoc.text(`Patient Outcome Report`, xOffset, y + 3);
        pdfDoc.setTextColor("#344054");
        y += 30;

        let intro = [
            ["Date of Birth", (payerType !== RD.PayerEnums.VA ? dateOfBirth : "N/A"), "Reporter", preparerName],
            ["Date of Injury", ((!dateOfInjury || dateOfInjury === "Unknown" || payerType === RD.PayerEnums.VA) ? "N/A" : dateOfInjury), "Report Date", moment(preparedDate).format("dddd MMMM D, YYYY")],
            ["Physician", physician === "" ? "N/A" : `${physician}, M.D.`, "Program Stage", R.isMonth6Report(reportWeek) ? "6 Months Post" : `Week ${Number(reportWeek)}`],
            ["Claim Number", ((claimNumber === "Unknown" || claimNumber === "") ? "N/A" : claimNumber), "Program Type", programType]
        ];

        pdfDoc.autoTable({
            pageBreak: "avoid",
            body: intro,
            startY: y + 10,
            theme: "plain",
            styles: { font: "Inter", fontSize: 10, textColor: "#344054", valign: "middle" },
            columnStyles: {
                0: { halign: "left", fontStyle: "bold" },
                1: { halign: "left" },
                2: { halign: "right", fontStyle: "bold" },
                3: { halign: "right" }
            }
        });
        y = pdfDoc.lastAutoTable.finalY;
        pdfDoc.setFontSize(12);

        // ---=== Key Improvements ===--- //

        if (selectedImprovedTrajectoryTypes?.length > 0) {
            // let chartingCanvas = getGraphCanvas(chartRef);
            // if (chartingCanvas) {
            y += 30;
            pdfDoc.setFillColor(234, 236, 240);
            pdfDoc.setDrawColor(234, 236, 240);
            pdfDoc.setTextColor(71, 84, 103);
            pdfDoc.line(40, y, ((pdfDoc.internal.pageSize.width / 2)) - (55), y);
            pdfDoc.line(((pdfDoc.internal.pageSize.width / 2)) + (56), y, (pageWidth - 40), y);
            xOffset = (pdfDoc.internal.pageSize.width / 2) - (pdfDoc.getStringUnitWidth(`Key Improvements`) * pdfDoc.internal.getFontSize() / 2);
            pdfDoc.text(`Key Improvements`, xOffset, y + 3);
            y += 30;

            let symbol = new Image();
            symbol.src = "/icons/info.png";
            pdfDoc.addImage(symbol, "png", 40, y, 10, 10);
            pdfDoc.setFontSize(10);
            pdfDoc.text("Measures in the graph have been converted to a standard percentage score, where higher is better.", 53, y + 8.5, { align: "left" });

            pdfDoc.setTextColor("#344054");
            y += 20;
            pdfDoc.addImage(keyImprovementsChartImageData, "png", 15, y, pageWidth - 30, 150);
            y += 150;

            let plainTrajectoryBody = []
            let colorizedTrajectoryBody = [];

            selectedImprovedTrajectoryTypes.forEach(
                (measureType: MD.MeasureType, index: number) => {
                    let improvedTrajectory = T.getTrajectoryOfType(measureType, allTrajectories);
                    let startMeasure = improvedTrajectory.measures[0];
                    let comparedMeasure = improvedTrajectory.comparedMeasure;
                    const startResolver = M.severityResolver(startMeasure);
                    const comparisonResolver = M.severityResolver(comparedMeasure);

                    const startColorCat = SR.getSeverityColor(measureType, startResolver.severityLevel);
                    const comparedColorCat = SR.getSeverityColor(measureType, comparisonResolver.severityLevel);

                    const startColor = startColorCat === 'severe' ? 'red' : startColorCat === 'warn' ? 'orange' : 'green';
                    const comparisonColor = comparedColorCat === 'severe' ? 'red' : comparedColorCat === 'warn' ? 'orange' : 'green';

                    const plainTrajectoryText = T.displayBandSeverityTrajectoryVerboseAssessment(improvedTrajectory);
                    plainTrajectoryBody.push({ content: plainTrajectoryText });

                    const scoreDescription = M.scoreDescription(comparedMeasure);
                    const scoreRangeDisplay = M.scoreRangeDisplay(comparedMeasure);
                    const measureDescription = `${scoreDescription ? [scoreDescription, scoreRangeDisplay].join(" ") : improvedTrajectory.measureName}`;
                    const colorizedTrajectoryText = [
                        `<${index}>${measureDescription}</${index}>`,
                        T.displayBandSeverityTrajectorySlope(improvedTrajectory),
                        `<${comparisonColor}>${M.displayAsUnits(comparedMeasure)} (${comparisonResolver.label})</${comparisonColor}>`,
                        T.displayBandSeverityTrajectoryApproach(improvedTrajectory),
                        `<${startColor}>${M.displayAsUnits(startMeasure)} (${startResolver.label})</${startColor}>`
                    ].join(" ");
                    colorizedTrajectoryBody.push(colorizedTrajectoryText);
                }
            );

            let cellIndex = 0;
            pdfDoc.autoTable({
                body: plainTrajectoryBody.map(
                    row => [row.content]
                ),
                willDrawCell: function (data: CellHookData) {
                    if (data.section === "body") {
                        const colorizedText: string = colorizedTrajectoryBody[cellIndex];
                        cellIndex++;
                        renderColorizedText(pdfDoc, data.cell, colorizedText);
                    }
                },
                startY: y + 10,
                theme: "plain",
                styles: { fontSize: 10, halign: "left" }
            });
            // }

            pdfDoc.addPage();
            y = 30;
        }

        // ---=== History of Injury ===--- //

        if (historyOfInjury?.length > 0) {
            pdfDoc.setFontSize(12);
            pdfDoc.setFillColor(234, 236, 240);
            pdfDoc.setDrawColor(234, 236, 240);
            pdfDoc.setTextColor(71, 84, 103);
            pdfDoc.line(40, y, ((pdfDoc.internal.pageSize.width / 2)) - (52), y);
            pdfDoc.line(((pdfDoc.internal.pageSize.width / 2)) + (52), y, (pageWidth - 40), y);
            xOffset = (pdfDoc.internal.pageSize.width / 2) - (pdfDoc.getStringUnitWidth(`Patient History`) * pdfDoc.internal.getFontSize() / 2);
            pdfDoc.text(`Patient History`, xOffset, y + 3);
            y += 10;

            pdfDoc.autoTable({
                pageBreak: "avoid",
                body: [["History of Injury"]],
                startY: y,
                theme: "plain",
                styles: { font: "Inter", fontSize: 12, textColor: [50, 96, 158], halign: "left", valign: "middle", fontStyle: "bold" }
            });
            y = pdfDoc.lastAutoTable.finalY;

            let pHeight = pdfDoc.internal.pageSize.height;

            pdfDoc.autoTable({
                pageBreak: "avoid",
                body: [[historyOfInjury]],
                startY: y += 2.5,
                theme: "plain",
                styles: { font: "Inter", fontSize: 10, textColor: "#344054", halign: "left" }
            });
            y = pdfDoc.lastAutoTable.finalY + 20;

            if (y % pHeight > (pHeight * (4 / 5))) {
                pdfDoc.addPage();
                y = 30;
            }
        }

        // ---=== Subjective Outcomes ===--- //

        header = [{ header: "Program Goals", dataKey: "goal" }, { header: "", dataKey: "detail" }];
        body = goals;

        if (goals?.length > 0 || discoveries?.length > 0 || includeEngagementLevel || includeGoalsProgress) {
            pdfDoc.setFontSize(12);
            pdfDoc.setFillColor(234, 236, 240);
            pdfDoc.setDrawColor(234, 236, 240);
            pdfDoc.setTextColor(71, 84, 103);
            pdfDoc.line(40, y, ((pdfDoc.internal.pageSize.width / 2)) - (65), y);
            pdfDoc.line(((pdfDoc.internal.pageSize.width / 2)) + (65), y, (pageWidth - 40), y);
            xOffset = (pdfDoc.internal.pageSize.width / 2) - (pdfDoc.getStringUnitWidth(`Subjective Outcomes`) * pdfDoc.internal.getFontSize() / 2);
            pdfDoc.text(`Subjective Outcomes`, xOffset, y + 3);
        }

        if (body.length > 0) {
            pdfDoc.autoTable({
                showHead: "firstPage",
                body: body,
                columns: header,
                startY: y += 30,
                theme: "plain",
                headStyles: { font: "Inter", fontSize: 12, textColor: [50, 96, 158], fontStyle: "bold" },
                columnStyles: {
                    goal: { cellWidth: 250, font: "Inter", fontSize: 10, fontStyle: "bold" },
                    detail: { cellWidth: 250, halign: "left", font: "Inter", fontSize: 10 }
                },
                bodyStyles: { cellPadding: 10, textColor: "#344054" }
            });

            y = pdfDoc.lastAutoTable.finalY;
        }

        // ---=== Perspective ===--- //

        header = [{ header: "Patient Perspective", dataKey: "perspective" }, { header: "", dataKey: "detail" }];
        const discoveriesList = Array.isArray(discoveries)
            ? discoveries.map(d => {
                if (typeof d === 'string') return d;
                if (d && typeof d === 'object' && 'text' in d) return String(d.text || '');
                return String(d || '');
            }).filter(text => text.length > 0)
            : (typeof discoveries === "string" ? (discoveries as string | undefined ?? "").split(",").map(s => s.trim()).filter(Boolean) : []);

        body = [
            includeGoalsProgress && goalsProgress && {
                perspective: "Perceived Progress Towards Goals",
                detail: `${goalsProgress.value}/10`
            },
            includeEngagementLevel && engagementLevel && {
                perspective: "Level of Engagement with Program",
                detail: `${engagementLevel.value}/10`
            },
            discoveriesList.length > 0 && {
                perspective: "Discoveries",
                detail: ""
            }
        ].filter(Boolean);

        if (body.length > 0) {
            pdfDoc.autoTable({
                pageBreak: "avoid",
                showHead: "firstPage",
                body: body,
                columns: header,
                startY: y += 20,
                theme: "plain",
                headStyles: { font: "Inter", fontSize: 12, textColor: [50, 96, 158], fontStyle: "bold" },
                bodyStyles: { cellPadding: 10, textColor: "#344054" },
                columnStyles: {
                    perspective: { cellWidth: 250, font: "Inter", fontSize: 10, fontStyle: "bold" },
                    detail: { cellWidth: 250, halign: "left", font: "Inter", fontSize: 10 }
                }
            });

            y = pdfDoc.lastAutoTable.finalY + 4;

            if (discoveriesList.length > 0) {
                // Build two-column rows: explicit bullet cell and text cell
                const discoveryRows = discoveriesList.map(d => ({ dot: "•", text: d }));

                // Base margins and available width identical to prior table
                const prior = pdfDoc.lastAutoTable;
                const marginLeft = prior?.settings?.margin?.left ?? 40;
                const marginRight = prior?.settings?.margin?.right ?? 40;

                // Indent visually from the previous table
                const indent = 14;
                const left = marginLeft + indent;

                // Compute widths that never overflow available space
                const pageWidth = pdfDoc.internal.pageSize.getWidth();
                const available = pageWidth - left - marginRight;

                const bulletCol = 16;                   // enough for the bullet glyph
                const gap = 2;                          // spacing between bullet and text inside cells
                const textCol = Math.max(0, available - bulletCol - gap);

                pdfDoc.autoTable({
                    pageBreak: "auto",                  // allow breaking to avoid overflow warnings
                    body: discoveryRows,
                    columns: [
                        { header: "", dataKey: "dot" },
                        { header: "", dataKey: "text" }
                    ],
                    startY: y,
                    theme: "plain",
                    margin: { left, right: marginRight },
                    styles: { cellPadding: 0, font: "Inter", fontSize: 10, textColor: "#344054", valign: "top" },
                    columnStyles: {
                        // Top valign + zero top padding aligns dot with first line of wrapped text
                        dot:  { cellWidth: bulletCol, halign: "center", font: "Inter", fontSize: 10, fontStyle: "bold", valign: "top", cellPadding: { top: 2, right: 0, bottom: 0, left: 0 } },
                        text: { cellWidth: textCol, halign: "left", font: "Inter", fontSize: 10, valign: "top" }
                    },
                    bodyStyles: {
                        // Add small left padding only on the text cell so the total width stays constant
                        cellPadding: { top: 2, right: 0, bottom: 4, left: gap }
                    }
                });

                y = pdfDoc.lastAutoTable.finalY + 10;
            }
        }

        // ---=== Subjective Progress Summary ===--- //

        header = [{ header: "Subjective Progress Summary", dataKey: "summary" }];
        body = [{ summary: subjectiveProgressSummary }];

        if (subjectiveProgressSummary?.length > 0) {
            pdfDoc.autoTable({
                pageBreak: "avoid",
                showHead: "firstPage",
                body: body,
                columns: header,
                startY: y += 10,
                theme: "plain",
                headStyles: { font: "Inter", fontSize: 12, textColor: [50, 96, 158], fontStyle: "bold" },
                bodyStyles: { fontSize: 10, font: "Inter", textColor: "#344054" }
            });

            y = pdfDoc.lastAutoTable.finalY + 20;
        }

        if (y % pdfDoc.internal.pageSize.height > (pdfDoc.internal.pageSize.height * (4 / 5))) {
            pdfDoc.addPage();
            y = 30;
        }

        // ---=== Objective Outcomes ===--- //

        if (includeSessionsAttended || includeUniqueDays || includeAverageTime || hasAnyIncludedTrajectories(includedTrajectories)) {
            pdfDoc.setFontSize(12);
            pdfDoc.setFillColor(234, 236, 240);
            pdfDoc.setDrawColor(234, 236, 240);
            pdfDoc.setTextColor(71, 84, 103);
            pdfDoc.line(40, y, ((pdfDoc.internal.pageSize.width / 2)) - (57), y);
            pdfDoc.line(((pdfDoc.internal.pageSize.width / 2)) + (61), y, (pageWidth - 40), y);
            xOffset = (pdfDoc.internal.pageSize.width / 2) - (pdfDoc.getStringUnitWidth(`Objective Outcomes`) * pdfDoc.internal.getFontSize() / 2);
            pdfDoc.text(`Objective Outcomes`, xOffset, y + 3);
        }

        header = [{ header: "Adherence", dataKey: "adherence" }, { header: "", dataKey: "detail" }];
        body = [
            (includeSessionsAttended && sessionsAttended) && {
                adherence: "Sessions Attended",
                detail: sessionsAttended
            },
            (includeUniqueDays && uniqueDays) && {
                adherence: "Avg. Days DME Usage per Week",
                detail: uniqueDays
            },
            (includeAverageTime && averageActivityTimePerWeek) && {
                adherence: "Avg. Time Spent in DME per Week",
                detail: averageActivityTimePerWeek
            }
        ].filter(Boolean);

        if (body.length > 0) {
            pdfDoc.autoTable({
                pageBreak: "avoid",
                showHead: "firstPage",
                body: body,
                columns: header,
                startY: y += 10,
                theme: "plain",
                headStyles: { font: "Inter", fontSize: 12, textColor: [50, 96, 158], fontStyle: "bold" },
                bodyStyles: { cellPadding: 10, textColor: "#344054" },
                columnStyles: {
                    adherence: { cellWidth: 250, font: "Inter", fontSize: 10, fontStyle: "bold" },
                    detail: { halign: "left", font: "Inter", fontSize: 10 }
                }
            });

            y = pdfDoc.lastAutoTable.finalY + 10;
        }

        header = [{ header: "Program Impact", dataKey: "impact" }, { header: "", dataKey: "detail" }];
        body = [
            (includeReturnToWork && returnToWorkStatus) && {
                impact: "Return to Work",
                detail: returnToWorkStatus.value === 0 ? "N/A" : returnToWorkStatus.label
            },
            (includeMedicationReduction && medicationReductionStatus) && {
                impact: "Medication Reduction",
                detail: medicationReductionStatus.label
            }
        ].filter(Boolean);

        if (body.length > 0) {
            pdfDoc.autoTable({
                pageBreak: "avoid",
                showHead: "firstPage",
                body: body,
                columns: header,
                startY: y += 10,
                theme: "plain",
                headStyles: { font: "Inter", fontSize: 12, textColor: [50, 96, 158], fontStyle: "bold" },
                bodyStyles: { cellPadding: 10, textColor: "#344054" },
                columnStyles: {
                    impact: { cellWidth: 250, font: "Inter", fontSize: 10, fontStyle: "bold" },
                    detail: { cellWidth: 250, halign: "left", font: "Inter", fontSize: 10 }
                }
            });

            y = pdfDoc.lastAutoTable.finalY;
        }

        let trajectoriesTableColumns: (MD.MeasureWeek)[] = createPdfTrajectoryTableColumns(reportWeek, reportPlanWeeks);

        body = createPdfTrajectoryRows(
            [
                MD.MeasureTypesMap.DASH,
                MD.MeasureTypesMap.NDI,
                MD.MeasureTypesMap.Oswestry,
                MD.MeasureTypesMap.QuickDASH,
                MD.MeasureTypesMap.WHODAS
            ],
            allTrajectories,
            includedTrajectories,
            trajectoriesTableColumns
        );

        if (body.length > 0) {

            if (y % pdfDoc.internal.pageSize.height > (pdfDoc.internal.pageSize.height * (4 / 5))) {
                pdfDoc.addPage();
                y = 30;
            }

            pdfDoc.autoTable({
                pageBreak: "avoid",
                body: [["Physical Disability"]],
                startY: y += 15,
                theme: "plain",
                styles: { font: "Inter", fontSize: 12, textColor: [50, 96, 158], halign: "left", valign: "middle", fontStyle: "bold" }
            });
            y = pdfDoc.lastAutoTable.finalY;

            header = [{ header: "Measure", dataKey: "trajectory" }];

            trajectoriesTableColumns.forEach(
                (measureWeek: MD.MeasureWeek) => (
                    header.push({ header: (M.isMonth6MeasureWeek(measureWeek) ? "6 Months Post" : `Week ${Number(measureWeek)}`), dataKey: `week${measureWeek}` })
                )
            );

            if (y % pdfDoc.internal.pageSize.height > (pdfDoc.internal.pageSize.height * (4 / 5))) {
                pdfDoc.addPage();
                y = 30;
            }

            pdfDoc.autoTable({
                columns: header,
                body: body,
                rowPageBreak: "avoid",
                startY: y += 15,
                tableLineWidth: 1,
                tableLineColor: "#eaecf0",
                theme: "grid",
                // Styles
                headStyles: { cellPadding: { top: 5, bottom: 0 }, fillColor: "#f9fafb", fontSize: 12, halign: "center", lineColor: "#eaecf0", lineWidth: 1, minCellHeight: 16, textColor: "#475467", valign: "middle" },
                columnStyles: { trajectory: { cellWidth: 180 }, default: { cellPadding: 3, fontSize: 10, halign: "center", minCellHeight: 55 } },
                bodyStyles: { fillColor: false, fontSize: 12, halign: "center", lineColor: "#eaecf0", lineWidth: 1, minCellHeight: 55, textColor: "#344054", valign: "middle" },
                // Custom Drawing
                willDrawCell: function (data: CellHookData) {
                    if (data.section === "body") {
                        renderPdfTrajectoryCell(pdfDoc, data.column.index, data.cell);
                    }
                }
            });

            y = pdfDoc.lastAutoTable.finalY;
        }

        body = createPdfTrajectoryRows(
            [
                MD.MeasureTypesMap.Activity,
                MD.MeasureTypesMap.KarunaRating,
                MD.MeasureTypesMap.LEFS10,
                MD.MeasureTypesMap.Pain,
                MD.MeasureTypesMap.PCSS,
                MD.MeasureTypesMap.RPQ,
                MD.MeasureTypesMap.RPQ3,
                MD.MeasureTypesMap.RPQ13,
                MD.MeasureTypesMap.Sleep,
                MD.MeasureTypesMap.UEFI
            ],
            allTrajectories,
            includedTrajectories,
            trajectoriesTableColumns
        );

        if (body.length > 0) {

            if (y % pdfDoc.internal.pageSize.height > (pdfDoc.internal.pageSize.height * (4 / 5))) {
                pdfDoc.addPage();
                y = 30;
            }

            pdfDoc.autoTable({
                pageBreak: "avoid",
                body: [["Physical Function"]],
                startY: y += 10,
                theme: "plain",
                styles: { font: "Inter", fontSize: 12, textColor: [50, 96, 158], halign: "left", valign: "middle", fontStyle: "bold" }
            });
            y = pdfDoc.lastAutoTable.finalY;

            if (includedTrajectories[MD.MeasureTypesMap.KarunaRating]) {
                if (y % pdfDoc.internal.pageSize.height > (pdfDoc.internal.pageSize.height * (4 / 5))) {
                    pdfDoc.addPage();
                    y = 30;
                }
                else {
                    y += 5;
                }
                let symbol = new Image();
                symbol.src = "/icons/info.png";
                pdfDoc.addImage(symbol, "png", 40, y, 7, 7);
                pdfDoc.setFontSize(10);

                pdfDoc.autoTable({
                    pageBreak: "avoid",
                    showHead: "firstPage",
                    body: [[`Karuna Rating is a bespoke measure of function across multiple VET™ movement activities. Range of motion is combined with functional capability demonstrated in each activity to produce a single score.`]],
                    startY: y - 5,
                    theme: "plain",
                    bodyStyles: { fontSize: 8, font: "Inter", textColor: "#344053" },
                    margin: {
                        left: 45
                    }
                });
                y = pdfDoc.lastAutoTable.finalY;
            }

            header = [{ header: "Measure", dataKey: "trajectory" }];

            trajectoriesTableColumns.forEach(
                (measureWeek: MD.MeasureWeek) => {
                    header.push({ header: (M.isMonth6MeasureWeek(measureWeek) ? "6 Months Post" : `Week ${Number(measureWeek)}`), dataKey: `week${measureWeek}` })
                }
            );

            if (y % pdfDoc.internal.pageSize.height > (pdfDoc.internal.pageSize.height * (4 / 5))) {
                pdfDoc.addPage();
                y = 30;
            }

            pdfDoc.autoTable({
                columns: header,
                body: body,
                rowPageBreak: "avoid",
                startY: y += 15,
                tableLineWidth: 1,
                tableLineColor: "#eaecf0",
                theme: "grid",
                // Styles
                headStyles: { cellPadding: { top: 5, bottom: 0 }, fillColor: "#f9fafb", fontSize: 12, halign: "center", lineColor: "#eaecf0", lineWidth: 1, minCellHeight: 16, textColor: "#475467", valign: "middle" },
                columnStyles: { trajectory: { cellWidth: 180 }, default: { cellPadding: 3, fontSize: 10, halign: "center", minCellHeight: 55 } },
                bodyStyles: { fillColor: false, fontSize: 12, halign: "center", lineColor: "#eaecf0", lineWidth: 1, minCellHeight: 55, textColor: "#344054", valign: "middle" },
                // Custom Drawing
                willDrawCell: function (data: CellHookData) {
                    if (data.section === "body") {
                        renderPdfTrajectoryCell(pdfDoc, data.column.index, data.cell);
                    }
                }
            });

            y = pdfDoc.lastAutoTable.finalY;
        }

        body = createPdfTrajectoryRows(
            [
                MD.MeasureTypesMap.LeftAbduction,
                MD.MeasureTypesMap.LeftFlexion,
                MD.MeasureTypesMap.LeftScaption,
                MD.MeasureTypesMap.RightAbduction,
                MD.MeasureTypesMap.RightFlexion,
                MD.MeasureTypesMap.RightScaption,
                MD.MeasureTypesMap.LowerFlexion,
                MD.MeasureTypesMap.NeckFlexion,
                MD.MeasureTypesMap.NeckExtension,
                MD.MeasureTypesMap.NeckLeftRotation,
                MD.MeasureTypesMap.NeckRightRotation
            ],
            allTrajectories,
            includedTrajectories
        );

        if (body.length > 0) {

            if (y % pdfDoc.internal.pageSize.height > (pdfDoc.internal.pageSize.height * (3 / 5))) {
                pdfDoc.addPage();
                y = 30;
            }

            pdfDoc.autoTable({
                pageBreak: "avoid",
                body: [["Range of Motion"]],
                startY: y += 15,
                theme: "plain",
                styles: { font: "Inter", fontSize: 12, textColor: [50, 96, 158], halign: "left", valign: "middle", fontStyle: "bold" },
                headStyles: { fillColor: "#f9fafb", fontSize: 12, halign: "center", lineColor: "#eaecf0", lineWidth: 1, textColor: "#475467", valign: "middle" }
            });
            y = pdfDoc.lastAutoTable.finalY;

            header = [
                { header: "Measure", dataKey: "trajectory" },
                { header: "First Measurement", dataKey: "firstMeasure" },
                { header: "Recent Best", dataKey: "recentMeasure" }
            ];

            pdfDoc.autoTable({
                head: [["Measure", "First Measurement", "Recent Best"]],
                columns: header,
                body: body,
                rowPageBreak: "avoid",
                startY: y += 15,
                tableLineWidth: 1,
                tableLineColor: "#eaecf0",
                theme: "grid",
                // Styles
                headStyles: { cellPadding: { top: 5, bottom: 0 }, fillColor: "#f9fafb", fontSize: 12, halign: "center", lineColor: "#eaecf0", lineWidth: 1, minCellHeight: 16, textColor: "#475467", valign: "middle" },
                columnStyles: { trajectory: { cellWidth: 240 }, default: { cellPadding: 3, fontSize: 10, halign: "center", minCellHeight: 55 } },
                bodyStyles: { fillColor: false, fontSize: 12, halign: "center", lineColor: "#eaecf0", lineWidth: 1, minCellHeight: 55, textColor: "#344054", valign: "middle" },
                // Custom Drawing
                willDrawCell: function (data: CellHookData) {
                    if (data.section === "body") {
                        renderPdfTrajectoryCell(pdfDoc, data.column.index, data.cell);
                    }
                }
            });

            y = pdfDoc.lastAutoTable.finalY;
        }

        body = createPdfTrajectoryRows(
            [
                MD.MeasureTypesMap.BriefPCS,
                MD.MeasureTypesMap.Confidence,
                MD.MeasureTypesMap.FABQActivity,
                MD.MeasureTypesMap.FABQTotal,
                MD.MeasureTypesMap.FABQWork,
                MD.MeasureTypesMap.GAD7,
                MD.MeasureTypesMap.Mood,
                MD.MeasureTypesMap.PCS,
                MD.MeasureTypesMap.PHQ9,
                MD.MeasureTypesMap.PSEQ,
                MD.MeasureTypesMap.PSEQ2
            ],
            allTrajectories,
            includedTrajectories,
            trajectoriesTableColumns
        );

        if (body.length > 0) {

            if (y % pdfDoc.internal.pageSize.height > (pdfDoc.internal.pageSize.height * (4 / 5))) {
                pdfDoc.addPage();
                y = 30;
            }

            pdfDoc.autoTable({
                body: [["Behavioral"]],
                pageBreak: "avoid",
                startY: y += 15,
                theme: "plain",
                styles: { font: "Inter", fontSize: 12, textColor: [50, 96, 158], halign: "left", valign: "middle", fontStyle: "bold" }
            });
            y = pdfDoc.lastAutoTable.finalY;

            header = [{ header: "Measure", dataKey: "trajectory" }];

            trajectoriesTableColumns.forEach(
                (measureWeek: MD.MeasureWeek) => {
                    header.push({ header: (M.isMonth6MeasureWeek(measureWeek) ? "6 Months Post" : `Week ${Number(measureWeek)}`), dataKey: `week${measureWeek}` })
                }
            );

            if (y % pdfDoc.internal.pageSize.height > (pdfDoc.internal.pageSize.height * (4 / 5))) {
                pdfDoc.addPage();
                y = 30;
            }

            pdfDoc.autoTable({
                columns: header,
                body: body,
                rowPageBreak: "avoid",
                startY: y += 15,
                tableLineWidth: 1,
                tableLineColor: "#eaecf0",
                theme: "grid",
                // Styles
                headStyles: { cellPadding: { top: 5, bottom: 0 }, fillColor: "#f9fafb", fontSize: 12, halign: "center", lineColor: "#eaecf0", lineWidth: 1, minCellHeight: 16, textColor: "#475467", valign: "middle" },
                columnStyles: { trajectory: { cellWidth: 180 }, default: { cellPadding: 3, fontSize: 10, halign: "center", minCellHeight: 55 } },
                bodyStyles: { fillColor: false, fontSize: 12, halign: "center", lineColor: "#eaecf0", lineWidth: 1, minCellHeight: 55, textColor: "#344054", valign: "middle" },
                // Custom Drawing
                willDrawCell: function (data: CellHookData) {
                    if (data.section === "body") {
                        renderPdfTrajectoryCell(pdfDoc, data.column.index, data.cell);
                    }
                }
            });

            y = pdfDoc.lastAutoTable.finalY;
        }

        body = createPdfTrajectoryRows(
            [
                MD.MeasureTypesMap.SocialFunction
            ],
            allTrajectories,
            includedTrajectories,
            trajectoriesTableColumns
        );

        if (body.length > 0) {

            if (y % pdfDoc.internal.pageSize.height > (pdfDoc.internal.pageSize.height * (4 / 5))) {
                pdfDoc.addPage();
                y = 30;
            }

            pdfDoc.autoTable({
                body: [["Social"]],
                pageBreak: "avoid",
                startY: y += 15,
                theme: "plain",
                styles: { font: "Inter", fontSize: 12, textColor: [50, 96, 158], halign: "left", valign: "middle", fontStyle: "bold" }
            });
            y = pdfDoc.lastAutoTable.finalY;

            header = [{ header: "Measure", dataKey: "trajectory" }];

            trajectoriesTableColumns.forEach(
                (measureWeek: MD.MeasureWeek) => {
                    header.push({ header: (M.isMonth6MeasureWeek(measureWeek) ? "6 Months Post" : `Week ${Number(measureWeek)}`), dataKey: `week${measureWeek}` })
                }
            );

            if (y % pdfDoc.internal.pageSize.height > (pdfDoc.internal.pageSize.height * (4 / 5))) {
                pdfDoc.addPage();
                y = 30;
            }

            pdfDoc.autoTable({
                columns: header,
                body: body,
                rowPageBreak: "avoid",
                startY: y += 15,
                tableLineWidth: 1,
                tableLineColor: "#eaecf0",
                theme: "grid",
                // Styles
                headStyles: { cellPadding: { top: 5, bottom: 0 }, fillColor: "#f9fafb", fontSize: 12, halign: "center", lineColor: "#eaecf0", lineWidth: 1, minCellHeight: 16, textColor: "#475467", valign: "middle" },
                columnStyles: { trajectory: { cellWidth: 180 }, default: { cellPadding: 3, fontSize: 10, halign: "center", minCellHeight: 55 } },
                bodyStyles: { fillColor: false, fontSize: 12, halign: "center", lineColor: "#eaecf0", lineWidth: 1, minCellHeight: 55, textColor: "#344054", valign: "middle" },
                // Custom Drawing
                willDrawCell: function (data: CellHookData) {
                    if (data.section === "body") {
                        renderPdfTrajectoryCell(pdfDoc, data.column.index, data.cell);
                    }
                }
            });

            y = pdfDoc.lastAutoTable.finalY;
        }

        // ---=== Assessments ===--- //

        y += 30;

        if (includePhysicalAssessments || includeBehavioralAssessments || includeSocialAssessments || includeContinuedNeedsAssessments) {
            if (y % pdfDoc.internal.pageSize.height > (pdfDoc.internal.pageSize.height * (4 / 5))) {
                pdfDoc.addPage();
                y = 30;
            }

            pdfDoc.setFontSize(12);
            pdfDoc.setFillColor(234, 236, 240);
            pdfDoc.setDrawColor(234, 236, 240);
            pdfDoc.setTextColor(71, 84, 103);
            pdfDoc.line(40, y, ((pdfDoc.internal.pageSize.width / 2)) - (37), y);
            pdfDoc.line(((pdfDoc.internal.pageSize.width / 2)) + (37), y, (pageWidth - 40), y);
            xOffset = (pdfDoc.internal.pageSize.width / 2) - (pdfDoc.getStringUnitWidth(`Assessments`) * pdfDoc.internal.getFontSize() / 2);
            pdfDoc.text(`Assessments`, xOffset, y + 3);
            y += 10;

            if (includePhysicalAssessments && physicalAssessments?.length > 0) {
                header = [{ header: "Physical Assessment", dataKey: "assessment" }, { header: "", dataKey: "detail" }];

                pdfDoc.autoTable({
                    pageBreak: "avoid",
                    showHead: "firstPage",
                    body: [[physicalAssessments]],
                    columns: header,
                    startY: y,
                    theme: "plain",
                    bodyStyles: { fontSize: 10, font: "Inter", textColor: "#344054" },
                    headStyles: { font: "Inter", fontSize: 12, textColor: [50, 96, 158], fontStyle: "bold" }
                });
                y = pdfDoc.lastAutoTable.finalY + 10;
            }

            if (includeBehavioralAssessments && behavioralAssessments?.length > 0) {
                header = [{ header: "Behavioral Assessment", dataKey: "assessment" }, { header: "", dataKey: "detail" }];

                pdfDoc.autoTable({
                    pageBreak: "avoid",
                    showHead: "firstPage",
                    body: [[behavioralAssessments]],
                    columns: header,
                    startY: y,
                    theme: "plain",
                    bodyStyles: { fontSize: 10, font: "Inter", textColor: "#344054" },
                    headStyles: { font: "Inter", fontSize: 12, textColor: [50, 96, 158], fontStyle: "bold" }
                });

                y = pdfDoc.lastAutoTable.finalY + 10;
            }

            if (includeSocialAssessments && socialAssessments?.length > 0) {
                header = [{ header: "Social Assessment", dataKey: "assessment" }, { header: "", dataKey: "detail" }];

                pdfDoc.autoTable({
                    pageBreak: "avoid",
                    showHead: "firstPage",
                    body: [[socialAssessments]],
                    columns: header,
                    startY: y,
                    theme: "plain",
                    bodyStyles: { fontSize: 10, font: "Inter", textColor: "#344054" },
                    headStyles: { font: "Inter", fontSize: 12, textColor: [50, 96, 158], fontStyle: "bold" }
                });

                y = pdfDoc.lastAutoTable.finalY + 10;
            }

            if (includeContinuedNeedsAssessments && continuedNeedsAssessments?.length > 0) {
                header = [{ header: "Continued Need", dataKey: "assessment" }, { header: "", dataKey: "detail" }];

                pdfDoc.autoTable({
                    pageBreak: "avoid",
                    showHead: "firstPage",
                    body: [[continuedNeedsAssessments]],
                    columns: header,
                    startY: y,
                    theme: "plain",
                    bodyStyles: { fontSize: 10, font: "Inter", textColor: "#344054" },
                    headStyles: { font: "Inter", fontSize: 12, textColor: [50, 96, 158], fontStyle: "bold" }
                });

                y = pdfDoc.lastAutoTable.finalY + 10;
            }
        }

        y += 10;

        body = [{ summary: plan }];

        if (plan.length > 0) {
            if ((y + 30) % pdfDoc.internal.pageSize.height > (pdfDoc.internal.pageSize.height * (4 / 5))) {
                pdfDoc.addPage();
                y = 30;
            }

            pdfDoc.setFontSize(12);
            pdfDoc.setFillColor(234, 236, 240);
            pdfDoc.setDrawColor(234, 236, 240);
            pdfDoc.setTextColor(71, 84, 103);
            pdfDoc.line(40, y, ((pdfDoc.internal.pageSize.width / 2)) - (15), y);
            pdfDoc.line(((pdfDoc.internal.pageSize.width / 2)) + (15), y, (pageWidth - 40), y);
            xOffset = (pdfDoc.internal.pageSize.width / 2) - (pdfDoc.getStringUnitWidth(`Plan`) * pdfDoc.internal.getFontSize() / 2);
            pdfDoc.text(`Plan`, xOffset, y + 3);
            y += 10;

            pdfDoc.autoTable({
                pageBreak: "avoid",
                showHead: "firstPage",
                body: body,
                startY: y,
                theme: "plain",
                bodyStyles: { fontSize: 10, font: "Inter", textColor: "#344054" }
            });
        }

        if (hasPhysicianOversight) {
            try {
                generateProviderSection(pdfDoc, patientName, coach);
            }
            catch (error) {
                console.error("Error in generateProviderSection:", error);
                throw error;
            }
        }

        numberReportPages(pdfDoc);

        return pdfDoc
    }
    catch (error) {
        console.error("PDF Generation Error:", error);
        throw error;
    }
};